/**
 * Structured logger for application-wide error and info logging.
 * Provides consistent logging format with context and error codes.
 */

export enum LogLevel {
	ERROR = 'ERROR',
	WARN = 'WARN',
	INFO = 'INFO',
	DEBUG = 'DEBUG'
}

export enum ErrorCode {
	// Page rendering errors
	PAGE_RENDER_ERROR = 'PAGE_RENDER_ERROR',
	PAGE_FETCH_ERROR = 'PAGE_FETCH_ERROR',
	PAGE_NOT_FOUND = 'PAGE_NOT_FOUND',

	// Static generation errors
	STATIC_PROPS_ERROR = 'STATIC_PROPS_ERROR',
	STATIC_PATHS_ERROR = 'STATIC_PATHS_ERROR',

	// GraphQL errors
	GRAPHQL_ERROR = 'GRAPHQL_ERROR',
	GRAPHQL_NETWORK_ERROR = 'GRAPHQL_NETWORK_ERROR',

	// Authentication & Authorization
	AUTH_ERROR = 'AUTH_ERROR',
	PASSWORD_PROTECTION_ERROR = 'PASSWORD_PROTECTION_ERROR',

	// Data processing errors
	DATA_TRANSFORM_ERROR = 'DATA_TRANSFORM_ERROR',
	VALIDATION_ERROR = 'VALIDATION_ERROR',

	// Generic
	UNKNOWN_ERROR = 'UNKNOWN_ERROR'
}

interface LogContext {
	[key: string]: unknown;
	slug?: string;
	locale?: string;
	path?: string;
	component?: string;
	userId?: string;
	requestId?: string;
}

interface LogEntry {
	timestamp: string;
	level: LogLevel;
	code?: ErrorCode;
	message: string;
	context?: LogContext;
	error?: {
		name: string;
		message: string;
		stack?: string;
	};
	environment: string;
}

class Logger {
	private isDevelopment: boolean;
	private isServer: boolean;

	constructor() {
		this.isDevelopment = process.env.NODE_ENV === 'development';
		this.isServer = typeof window === 'undefined';
	}

	/**
	 * Formats a log entry into a structured JSON format
	 */
	private formatLogEntry(
		level: LogLevel,
		message: string,
		context?: LogContext,
		error?: Error,
		code?: ErrorCode
	): LogEntry {
		const entry: LogEntry = {
			timestamp: new Date().toISOString(),
			level,
			message,
			environment: process.env.NODE_ENV || 'development',
			...(code && { code }),
			...(context && { context }),
			...(error && {
				error: {
					name: error.name,
					message: error.message,
					...(this.isDevelopment && { stack: error.stack })
				}
			})
		};

		return entry;
	}

	/**
	 * Outputs the log entry to appropriate destination
	 */
	private output(entry: LogEntry): void {
		const formatted = JSON.stringify(entry, null, this.isDevelopment ? 2 : 0);

		// In development, pretty print to console
		if (this.isDevelopment) {
			const color = this.getColorForLevel(entry.level);
			console.log(`${color}[${entry.level}]${'\x1b[0m'} ${entry.message}`);
			console.log(formatted);
		} else {
			// In production, output as single-line JSON for log aggregation
			console.log(formatted);
		}

		// TODO: Add integration with external logging service (e.g., Sentry, DataDog, CloudWatch)
		// if (!this.isDevelopment) {
		//   this.sendToExternalService(entry);
		// }
	}

	/**
	 * Returns ANSI color code for log level
	 */
	private getColorForLevel(level: LogLevel): string {
		switch (level) {
			case LogLevel.ERROR:
				return '\x1b[31m'; // Red
			case LogLevel.WARN:
				return '\x1b[33m'; // Yellow
			case LogLevel.INFO:
				return '\x1b[36m'; // Cyan
			case LogLevel.DEBUG:
				return '\x1b[90m'; // Gray
			default:
				return '\x1b[0m'; // Reset
		}
	}

	/**
	 * Log an error with context
	 */
	public error(message: string, error?: Error, context?: LogContext, code?: ErrorCode): void {
		const entry = this.formatLogEntry(LogLevel.ERROR, message, context, error, code || ErrorCode.UNKNOWN_ERROR);
		this.output(entry);
	}

	/**
	 * Log a warning
	 */
	public warn(message: string, context?: LogContext, code?: ErrorCode): void {
		const entry = this.formatLogEntry(LogLevel.WARN, message, context, undefined, code);
		this.output(entry);
	}

	/**
	 * Log informational message
	 */
	public info(message: string, context?: LogContext): void {
		const entry = this.formatLogEntry(LogLevel.INFO, message, context);
		this.output(entry);
	}

	/**
	 * Log debug message (only in development)
	 */
	public debug(message: string, context?: LogContext): void {
		if (this.isDevelopment) {
			const entry = this.formatLogEntry(LogLevel.DEBUG, message, context);
			this.output(entry);
		}
	}

	/**
	 * Helper to log page rendering errors with common context
	 */
	public pageError(
		message: string,
		error: Error,
		slug?: string,
		locale?: string,
		additionalContext?: LogContext
	): void {
		this.error(
			message,
			error,
			{
				slug,
				locale,
				...additionalContext
			},
			ErrorCode.PAGE_RENDER_ERROR
		);
	}

	/**
	 * Helper to log GraphQL errors
	 */
	public graphqlError(message: string, error: Error, query?: string, variables?: unknown): void {
		this.error(
			message,
			error,
			{
				query,
				variables
			},
			ErrorCode.GRAPHQL_ERROR
		);
	}

	/**
	 * Helper to log static generation errors
	 */
	public staticPropsError(message: string, error: Error, slug?: string, locale?: string, params?: unknown): void {
		this.error(
			message,
			error,
			{
				slug,
				locale,
				params
			},
			ErrorCode.STATIC_PROPS_ERROR
		);
	}
}

// Export singleton instance
export const logger = new Logger();

export default logger;
